import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ImageLightboxService } from '@ds/core';
import { YouTubeContentBlock } from '@ds/interfaces';

@Component({
  selector: 'ds-cb-youtube',
  template: `
    <div *ngIf="!isVideoLoaded" class="placeholder">
      <img [src]="imageLink" alt="YouTube video placeholder" />

      <div class="overlay" *ngIf="!hasConsent">
        <p>
          This video is hosted by YouTube. Clicking continue will allow YouTube
          content and cookies. More info at
          <a
            href="https://policies.google.com/technologies/cookies"
            target="_blank"
            >Google's cookie policy</a
          >.
        </p>
        <button type="button" (click)="grantConsent()">Continue</button>
      </div>

      <div class="overlay" *ngIf="hasConsent">
        <p>YouTube cookies have been accepted for this video.</p>
        <button type="button" (click)="loadVideo()">Play video</button>
      </div>
    </div>

    <iframe
      *ngIf="isVideoLoaded"
      [src]="trustedLink"
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowfullscreen
    >
    </iframe>
    <div class="edit-shield"></div>
  `,
  styles: [
    `
      :host {
        position: relative;
        padding-bottom: 56.25%;
        padding-top: 30px;
        height: 0;
        overflow: hidden;
        display: inline-block;
        width: 100%;
      }
      .placeholder {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: #000;
      }
      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        opacity: 0.55;
      }
      iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
      .overlay {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
        width: calc(100% - 2rem);
        max-width: 38rem;
        color: #fff;
        text-align: center;
        background: rgba(0, 0, 0, 0.7);
        padding: 1rem;
        border-radius: 0.5rem;
      }
      .overlay p {
        margin: 0 0 0.75rem;
      }
      .overlay button {
        appearance: none;
        border: 0;
        border-radius: 0.3rem;
        background: #d7263d;
        color: #fff;
        padding: 0.45rem 0.9rem;
        font-weight: 600;
        cursor: pointer;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContentBlockYouTubeComponent {
  block: YouTubeContentBlock;
  @Input() youTubeId: string;
  hasConsent = false;
  isVideoLoaded = false;
  trustedLink: SafeResourceUrl | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private lightBoxService: ImageLightboxService
  ) {}

  get imageLink() {
    const link = `https://img.youtube.com/vi/${this.youTubeId}/mqdefault.jpg`;
    return this.lightBoxService.getUrl(link, 640);
  }

  grantConsent() {
    this.hasConsent = true;
  }

  loadVideo() {
    if (!this.youTubeId) {
      return;
    }

    this.trustedLink = this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube-nocookie.com/embed/${this.youTubeId}?autoplay=1&rel=0`
    );
    this.isVideoLoaded = true;
  }

  init(block: YouTubeContentBlock) {
    this.block = block;
    this.youTubeId = block.config.youTubeId || '';
    this.hasConsent = false;
    this.isVideoLoaded = false;
    this.trustedLink = null;
  }
}
